import React from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const LoaderShell = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: "calc(100vh - 120px)",
  display: "grid",
  placeItems: "center",
  overflow: "hidden",
  padding: theme.spacing(3),
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.primary.main, 0.08)} 0%,
    ${alpha(theme.palette.background.default, 0.96)} 45%,
    ${alpha("#ffffff", 0.98)} 100%)`,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -120,
    background:
      "radial-gradient(circle at 20% 20%, rgba(110, 21, 30, 0.16), transparent 28%), radial-gradient(circle at 80% 30%, rgba(110, 21, 30, 0.1), transparent 24%), radial-gradient(circle at 50% 85%, rgba(110, 21, 30, 0.08), transparent 26%)",
    filter: "blur(12px)",
    animation: "loader-float 9s ease-in-out infinite alternate",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(110, 21, 30, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(110, 21, 30, 0.035) 1px, transparent 1px)",
    backgroundSize: "36px 36px",
    maskImage:
      "radial-gradient(circle at center, black 40%, transparent 85%)",
    opacity: 0.5,
    pointerEvents: "none",
  },
  "@keyframes loader-float": {
    "0%": { transform: "translate3d(-12px, -8px, 0) scale(1)" },
    "100%": { transform: "translate3d(12px, 14px, 0) scale(1.03)" },
  },
}));

const LoaderCard = styled(Paper)(({ theme }) => ({
  position: "relative",
  width: "min(640px, 100%)",
  borderRadius: 28,
  padding: theme.spacing(4),
  overflow: "hidden",
  boxShadow: "0 24px 70px rgba(24, 24, 24, 0.12)",
  background: `linear-gradient(180deg, ${alpha(
    theme.palette.background.paper,
    0.96
  )} 0%, ${alpha("#ffffff", 0.92)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  backdropFilter: "blur(16px)",
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    borderRadius: 22,
    padding: theme.spacing(3),
  },
}));

const LoaderPulse = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(
    theme.palette.primary.main,
    0.72
  )})`,
  boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
}));

const Loader = () => {
  const location = useLocation();

  const pageMeta = React.useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("order")) {
      return {
        title: "Loading orders",
        subtitle: "Fetching live tickets and kitchen activity.",
      };
    }
    if (path.includes("dashboard")) {
      return {
        title: "Loading dashboard",
        subtitle: "Refreshing revenue, feedback, and menu insights.",
      };
    }
    if (path.includes("settings")) {
      return {
        title: "Loading settings",
        subtitle: "Restoring business details and preferences.",
      };
    }
    if (path.includes("drinks") || path.includes("food")) {
      return {
        title: "Loading menu",
        subtitle: "Preparing product lists, categories, and stock filters.",
      };
    }
    if (path.includes("campaign") || path.includes("combo")) {
      return {
        title: "Loading promotions",
        subtitle: "Preparing loyalty and combo data.",
      };
    }

    return {
      title: "Loading workspace",
      subtitle: "Preparing your restaurant management tools.",
    };
  }, [location.pathname]);

  return (
    <LoaderShell>
      <LoaderCard elevation={0}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(120deg, rgba(110, 21, 30, 0.08), transparent 40%, rgba(110, 21, 30, 0.04))",
            pointerEvents: "none",
          }}
        />
        <Stack spacing={3} sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <LoaderPulse>
              <CircularProgress
                size={34}
                thickness={4}
                sx={{ color: "#fff" }}
              />
            </LoaderPulse>
            <Box>
              <Typography variant="overline" color="primary.main">
                Menutender
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {pageMeta.title}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: "relative" }}>
            <LinearProgress
              sx={{
                height: 10,
                borderRadius: 999,
                backgroundColor: "rgba(110, 21, 30, 0.08)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "text.secondary", maxWidth: 520 }}
            >
              {pageMeta.subtitle}
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={18} width="42%" />
            <Skeleton variant="rounded" height={18} width="68%" />
            <Skeleton variant="rounded" height={18} width="55%" />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={120} />
          </Box>
        </Stack>
      </LoaderCard>
    </LoaderShell>
  );
};

export default Loader;
